import * as THREE from 'three';

export interface FeedbackConfig {
  enableVisualEffects: boolean;
  enableAudioEffects: boolean;
  clickEffectDuration: number;
  selectionOutlineColor: number;
  hoverOutlineColor: number;
}

const DEFAULT_CONFIG: FeedbackConfig = {
  enableVisualEffects: true,
  enableAudioEffects: false, // Start with visual only
  clickEffectDuration: 300, // ms
  selectionOutlineColor: 0x00ff88,
  hoverOutlineColor: 0x444444,
};

interface VisualEffect {
  id: string;
  object: THREE.Object3D;
  startTime: number;
  duration: number;
  originalScale: THREE.Vector3;
  originalPosition?: THREE.Vector3;
  originalRotation?: THREE.Euler;
  originalColor?: THREE.Color;
  type: 'click_ripple' | 'selection_outline' | 'hover_glow' | 'touch_pulse' | 'long_press_ring' | 'selection_particles';
}

export class FeedbackManager {
  private canvas: HTMLCanvasElement;
  private config: FeedbackConfig;
  
  // Visual effect tracking
  private activeEffects: Map<string, VisualEffect> = new Map();
  
  constructor(canvas: HTMLCanvasElement, config?: Partial<FeedbackConfig>) {
    this.canvas = canvas;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }
  
  public triggerClickFeedback(object: THREE.Object3D): void {
    if (!this.config.enableVisualEffects) return;
    
    // Create selection outline effect
    this.applySelectionOutline(object);
    
    // Create click ripple effect (simple scaling animation)
    this.createClickRipple(object);
    
    console.log('Click feedback triggered for object:', object.userData['id']);
  }
  
  public triggerHoverFeedback(_object: THREE.Object3D): void {
    if (!this.config.enableVisualEffects) return;
    
    // Apply hover outline (handled by SelectionManager for now)
    // Could add additional hover effects here
  }
  
  public clearHoverFeedback(_object: THREE.Object3D): void {
    // Clear hover effects (handled by SelectionManager for now)
  }

  public triggerTouchFeedback(object: THREE.Object3D): void {
    if (!this.config.enableVisualEffects) return;
    
    // Create touch pulse effect - more prominent than click
    this.createTouchPulse(object);
    
    console.log('Touch feedback triggered for object:', object.userData['id']);
  }
  
  public triggerLongPressFeedback(object: THREE.Object3D): void {
    if (!this.config.enableVisualEffects) return;
    
    // Create long press ring effect
    this.createLongPressRing(object);
    
    console.log('Long press feedback triggered for object:', object.userData['id']);
  }
  
  public triggerSelectionParticles(object: THREE.Object3D): void {
    if (!this.config.enableVisualEffects) return;
    
    // Create particle burst effect for enhanced selection feedback
    this.createSelectionParticles(object);
    
    console.log('Selection particles triggered for object:', object.userData['id']);
  }
  
  private createTouchPulse(object: THREE.Object3D): void {
    const effectId = `touch_pulse_${object.userData['id']}_${Date.now()}`;
    
    // Create a more prominent pulse effect for touch
    const effect: VisualEffect = {
      id: effectId,
      object: object,
      startTime: Date.now(),
      duration: 600, // Longer duration for touch
      originalScale: object.scale.clone(),
      originalPosition: object.position.clone(),
      type: 'touch_pulse',
    };
    
    this.activeEffects.set(effectId, effect);
  }
  
  private createLongPressRing(object: THREE.Object3D): void {
    const effectId = `long_press_ring_${object.userData['id']}_${Date.now()}`;
    
    // Create expanding ring effect for long press
    const effect: VisualEffect = {
      id: effectId,
      object: object,
      startTime: Date.now(),
      duration: 800, // Longer duration for emphasis
      originalScale: object.scale.clone(),
      originalPosition: object.position.clone(),
      type: 'long_press_ring',
    };
    
    this.activeEffects.set(effectId, effect);
  }
  
  private createSelectionParticles(object: THREE.Object3D): void {
    const effectId = `selection_particles_${object.userData['id']}_${Date.now()}`;
    
    // Create particle burst effect
    const effect: VisualEffect = {
      id: effectId,
      object: object,
      startTime: Date.now(),
      duration: 1000, // Longer duration for particle animation
      originalScale: object.scale.clone(),
      originalPosition: object.position.clone(),
      type: 'selection_particles',
    };
    
    this.activeEffects.set(effectId, effect);
  }
  
  private applySelectionOutline(object: THREE.Object3D): void {
    // For now, we'll use emissive material changes
    // In a more advanced implementation, we could use post-processing outlines
    
    if (object instanceof THREE.Mesh && object.material) {
      const material = object.material as THREE.Material;
      
      if ('emissive' in material) {
        const emissiveMaterial = material as THREE.MeshStandardMaterial;
        
        // Store original values if not already stored
        if (!object.userData['originalEmissive']) {
          object.userData['originalEmissive'] = emissiveMaterial.emissive.clone();
          object.userData['originalEmissiveIntensity'] = emissiveMaterial.emissiveIntensity;
        }
        
        // Apply selection color
        emissiveMaterial.emissive = new THREE.Color(this.config.selectionOutlineColor);
        emissiveMaterial.emissiveIntensity = 0.5;
      }
    }
  }
  
  private createClickRipple(object: THREE.Object3D): void {
    const effectId = `click_${object.userData['id']}_${Date.now()}`;
    
    // Create a scaling animation effect
    const effect: VisualEffect = {
      id: effectId,
      object: object,
      startTime: Date.now(),
      duration: this.config.clickEffectDuration,
      originalScale: object.scale.clone(),
      type: 'click_ripple',
    };
    
    this.activeEffects.set(effectId, effect);
  }
  
  public clearSelectionFeedback(object: THREE.Object3D): void {
    if (object instanceof THREE.Mesh && object.material) {
      const material = object.material as THREE.Material;
      
      if ('emissive' in material && object.userData['originalEmissive']) {
        const emissiveMaterial = material as THREE.MeshStandardMaterial;
        
        // Restore original emissive values
        emissiveMaterial.emissive = object.userData['originalEmissive'];
        emissiveMaterial.emissiveIntensity = object.userData['originalEmissiveIntensity'] || 0;
      }
    }
  }
  
  public update(_deltaTime: number): void {
    const now = Date.now();
    const effectsToRemove: string[] = [];
    
    // Update all active visual effects
    for (const [id, effect] of this.activeEffects) {
      const elapsed = now - effect.startTime;
      const progress = Math.min(elapsed / effect.duration, 1);
      
      if (progress >= 1) {
        // Effect is complete
        this.completeEffect(effect);
        effectsToRemove.push(id);
      } else {
        // Update effect
        this.updateEffect(effect, progress);
      }
    }
    
    // Remove completed effects
    for (const id of effectsToRemove) {
      this.activeEffects.delete(id);
    }
  }
  
  private updateEffect(effect: VisualEffect, progress: number): void {
    switch (effect.type) {
      case 'click_ripple':
        // Create a brief scale pulse effect
        const scaleMultiplier = 1 + Math.sin(progress * Math.PI) * 0.1;
        effect.object.scale.copy(effect.originalScale).multiplyScalar(scaleMultiplier);
        break;
        
      case 'touch_pulse':
        // Create a more prominent pulse effect for touch
        const touchPulse = 1 + Math.sin(progress * Math.PI * 2) * 0.15;
        effect.object.scale.copy(effect.originalScale).multiplyScalar(touchPulse);
        
        // Add slight position oscillation
        if (effect.originalPosition) {
          const oscillation = Math.sin(progress * Math.PI * 4) * 0.02;
          effect.object.position.copy(effect.originalPosition);
          effect.object.position.y += oscillation;
        }
        break;
        
      case 'long_press_ring':
        // Create expanding ring effect
        const ringScale = 1 + progress * 0.3;
        effect.object.scale.copy(effect.originalScale).multiplyScalar(ringScale);
        
        // Add rotation for ring effect
        if (effect.originalRotation) {
          effect.object.rotation.z = progress * Math.PI * 2;
        }
        
        // Fade out as it expands
        if (effect.object instanceof THREE.Mesh && effect.object.material) {
          const material = effect.object.material as THREE.Material;
          if ('opacity' in material) {
            (material as any).opacity = 1 - progress * 0.7;
          }
        }
        break;
        
      case 'selection_particles':
        // Create particle burst effect
        const particleScale = 1 + Math.sin(progress * Math.PI) * 0.2;
        effect.object.scale.copy(effect.originalScale).multiplyScalar(particleScale);
        
        // Add spinning effect
        effect.object.rotation.y = progress * Math.PI * 4;
        
        // Pulsating glow effect
        if (effect.object instanceof THREE.Mesh && effect.object.material) {
          const material = effect.object.material as THREE.Material;
          if ('emissive' in material) {
            const emissiveMaterial = material as THREE.MeshStandardMaterial;
            const glowIntensity = Math.sin(progress * Math.PI * 6) * 0.3 + 0.5;
            emissiveMaterial.emissiveIntensity = glowIntensity;
          }
        }
        break;
    }
  }
  
  private completeEffect(effect: VisualEffect): void {
    switch (effect.type) {
      case 'click_ripple':
        // Restore original scale
        effect.object.scale.copy(effect.originalScale);
        break;
        
      case 'touch_pulse':
        // Restore original scale and position
        effect.object.scale.copy(effect.originalScale);
        if (effect.originalPosition) {
          effect.object.position.copy(effect.originalPosition);
        }
        break;
        
      case 'long_press_ring':
        // Restore original scale and rotation
        effect.object.scale.copy(effect.originalScale);
        if (effect.originalRotation) {
          effect.object.rotation.copy(effect.originalRotation);
        }
        
        // Restore original opacity
        if (effect.object instanceof THREE.Mesh && effect.object.material) {
          const material = effect.object.material as THREE.Material;
          if ('opacity' in material) {
            (material as any).opacity = 1;
          }
        }
        break;
        
      case 'selection_particles':
        // Restore original scale and rotation
        effect.object.scale.copy(effect.originalScale);
        effect.object.rotation.y = effect.originalRotation ? effect.originalRotation.y : 0;
        
        // Restore original emissive intensity
        if (effect.object instanceof THREE.Mesh && effect.object.material) {
          const material = effect.object.material as THREE.Material;
          if ('emissive' in material) {
            const emissiveMaterial = material as THREE.MeshStandardMaterial;
            emissiveMaterial.emissiveIntensity = effect.object.userData['originalEmissiveIntensity'] || 0;
          }
        }
        break;
    }
  }
  
  public dispose(): void {
    // Complete all active effects
    for (const effect of this.activeEffects.values()) {
      this.completeEffect(effect);
    }
    
    this.activeEffects.clear();
  }
}